/**
 * 协调记录面板组件 - 与 BudgetOverviewTower 风格一致
 */
import React, { useState, useEffect } from 'react';
import { Spin, Empty, message, Tag } from 'antd';
import { CoordinationCostService } from '../../../services/coordinationCostService';
import { formatMoney } from '../../../utils/budgetUtils';
import { CoordinationCostType } from '../../../types';

const fmt = (n: number): string => {
  if (Math.abs(n) >= 10000) return `¥${(n / 10000).toFixed(1)}万`;
  return `¥${n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
};

interface CoordinationPanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
  constructionType?: string;
}

interface CoordinationRecord {
  _id: string;
  costDate: string;
  costType: CoordinationCostType;
  amount: number;
  description?: string;
  handler?: string;
  constructionType?: string | null;
}

const getTypeTag = (type: CoordinationCostType) => {
  const colorMap: Record<CoordinationCostType, string> = {
    [CoordinationCostType.MANAGEMENT]: '#547590',
    [CoordinationCostType.COORDINATION]: '#5d8a7f',
    [CoordinationCostType.MISC]: '#667986',
    [CoordinationCostType.EMERGENCY]: '#c46d5f',
  };

  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: `${colorMap[type]}18`,
      color: colorMap[type],
      border: `1px solid ${colorMap[type]}30`,
    }}>
      {CoordinationCostService.getCostTypeLabel(type)}
    </span>
  );
};

const CoordinationPanel: React.FC<CoordinationPanelProps> = ({
  projectId,
  isMobile = false,
  active = false,
  constructionType,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CoordinationRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const fetchData = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const response = await CoordinationCostService.getCoordinationCosts({
        projectId,
        constructionType,
        page: 1,
        limit: 500
      });

      if (response.success && response.data) {
        setData(response.data);
        setTotal(response.pagination?.total || response.data.length);
      }
    } catch (error) {
      console.error('获取协调记录失败:', error);
      message.error('获取协调记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId, constructionType]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{
        textAlign: 'center', padding: '40px 0',
        color: 'var(--text-muted)', fontSize: 13,
      }}>
        <Spin size="small" /> 加载中...
      </div>
    );
  }

  if (!data.length) {
    return (
      <div style={{
        textAlign: 'center', padding: '40px 16px',
        color: 'var(--text-muted)',
      }}>
        <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>{'🔗'}</div>
        <div style={{ fontSize: 13 }}>暂无协调记录</div>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .coordination-panel-row:hover { background: rgba(0,0,0,0.025) !important; }
      `}</style>

      <div style={{ borderRadius: '0 0 var(--radius-sm) var(--radius-sm)', overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
        {/* 表头 */}
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '90px 100px 60px 80px 100px',
            gap: 8,
            padding: '6px 12px',
            background: 'rgba(0,0,0,0.03)',
            borderBottom: '1px solid var(--border)',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>日期</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>类型</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>施工类型</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>经办人</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>金额</span>
          </div>
        )}
        {data.map((record, i) => (
          <div
            key={record._id}
            className="coordination-panel-row"
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr auto' : '90px 100px 60px 80px 100px',
              gap: isMobile ? 0 : 8,
              padding: isMobile ? '10px 12px' : '10px 12px',
              borderBottom: i < data.length - 1 ? '1px solid var(--border)' : 'none',
              alignItems: 'center',
              transition: 'background 0.15s',
              cursor: 'default',
            }}
          >
            {isMobile ? (
              <>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>
                    {getTypeTag(record.costType)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                    {formatDate(record.costDate)}{record.handler ? ` · ${record.handler}` : ''}
                  </div>
                  {record.description && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontStyle: 'italic' }}>
                      {record.description}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 13, fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-primary)', textAlign: 'right', minWidth: 80 }}>
                  {fmt(record.amount || 0)}
                </div>
              </>
            ) : (
              <>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>
                  {formatDate(record.costDate)}
                </span>
                <span>{getTypeTag(record.costType)}</span>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {record.constructionType || '—'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {record.handler || '-'}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-primary)', textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 }}>
                  {fmt(record.amount || 0)}
                </span>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      {total > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          padding: '8px 12px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-surface)',
        }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            共 {total} 条，已显示 {data.length} 条
          </span>
        </div>
      )}
    </div>
  );
};

export default CoordinationPanel;
